import { z } from 'zod';
import type { RepartoFilaDTO } from './costos.ts';
import { decimalPositivo, paginacion, uuid } from './comun.ts';

export const METODOS_GASTO = ['efectivo_usd', 'efectivo_bs', 'zelle', 'transferencia', 'pago_movil'] as const;
export type MetodoGasto = (typeof METODOS_GASTO)[number];

/** Reparto de un gasto entre productos (spec 16, sección 4): monto en $ por producto y entre cuántas unidades (nunca cero). */
export const repartoGastoInput = z.object({ producto_id: uuid, monto: decimalPositivo, unidades: decimalPositivo });
export type RepartoGastoInput = z.infer<typeof repartoGastoInput>;

const gastoBase = z.object({
  fecha: z.iso.date(),
  categoria_id: uuid,
  descripcion: z.string().trim().min(3, 'Describe el gasto').max(300),
  moneda: z.enum(['USD', 'BS']).default('USD'),
  monto: decimalPositivo,
  metodo: z.enum(METODOS_GASTO),
  soporte: z.string().trim().max(80).nullable().optional(),
  comentario: z.string().trim().max(300).nullable().optional(),
  /** Gasto personal de la dueña («no de la tienda»): se reporta aparte. */
  personal: z.boolean().default(false),
  /** A quién se pagó. */
  pagado_a: z.string().trim().min(2).max(120).nullable().optional(),
  /** Gastos no admite nada con factura fiscal: si la tiene, se registra en Compras (409 `gasto_con_factura`). */
  tiene_factura: z.boolean().default(false),
  /** «¿Este gasto es de algún producto?» Sí: suma al costo real con su reparto. No: gasto general (ganancia real). */
  es_de_producto: z.boolean().default(false),
  reparto: z.array(repartoGastoInput).max(300).default([]),
  /** Compra elegida para repartirlo entre lo que trajo (queda ligada: origen y aviso de repetido). */
  compra_id: uuid.nullable().optional(),
});
const REPARTO_COHERENTE = { message: 'Si el gasto es de algún producto, repártelo entre ellos', path: ['reparto'] };
const repartoCoherente = (g: { es_de_producto: boolean; reparto: unknown[] }) => g.es_de_producto === g.reparto.length > 0;
export const gastoInput = gastoBase.refine(repartoCoherente, REPARTO_COHERENTE);
export type GastoInput = z.infer<typeof gastoInput>;
/** Edición (reemplazo completo): moneda y ámbito son obligatorios; un cuerpo sin ellos no reclasifica el gasto. */
export const gastoEditarInput = gastoBase.extend({ moneda: z.enum(['USD', 'BS']), personal: z.boolean() }).refine(repartoCoherente, REPARTO_COHERENTE);
/** Cambiar a qué productos va un gasto ya aprobado y cuánto a cada uno: recalcula el costo (spec 16, 3.6). */
export const gastoRepartoInput = z.object({
  es_de_producto: z.boolean(),
  reparto: z.array(repartoGastoInput).max(300).default([]),
  compra_id: uuid.nullable().optional(),
  motivo: z.string().trim().min(3, 'Indica el motivo del cambio').max(300),
}).refine(repartoCoherente, REPARTO_COHERENTE);
export type GastoRepartoInput = z.infer<typeof gastoRepartoInput>;

export const resolverGastoInput = z.object({ comentario: z.string().trim().max(300).optional() });
/** Anulación de un gasto aprobado (solo la dueña): el motivo es obligatorio y queda en el listado y en la auditoría. */
export const anularGastoInput = z.object({ motivo: z.string().trim().min(3, 'Indica el motivo de la anulación').max(300) });
export type AnularGastoInput = z.infer<typeof anularGastoInput>;
export const categoriaGastoInput = z.object({ nombre: z.string().trim().min(2).max(60), orden: z.number().int().min(0).max(1000).default(0), activa: z.boolean().default(true) });
export type CategoriaGastoInput = z.infer<typeof categoriaGastoInput>;
/** Edición PARCIAL: sin los `.default()` del alta (desactivar una categoría le ponía orden 0; renombrarla la reactivaba). */
export const categoriaGastoEditarInput = z.object({ nombre: z.string().trim().min(2).max(60).optional(), orden: z.number().int().min(0).max(1000).optional(), activa: z.boolean().optional() });
export type CategoriaGastoEditarInput = z.infer<typeof categoriaGastoEditarInput>;

export const gastosFiltros = paginacion.extend({
  estado: z.enum(['pendiente', 'aprobado', 'rechazado', 'anulado']).optional(),
  categoria_id: uuid.optional(),
  desde: z.iso.date().optional(),
  hasta: z.iso.date().optional(),
  q: z.string().trim().max(80).optional(),
  sucursal_id: uuid.optional(),
  /** tienda = solo los de la tienda · personal = solo los personales. */
  ambito: z.enum(['tienda', 'personal']).optional(),
  /** true = asociados a productos · false = gastos generales. */
  asociado: z.enum(['true', 'false']).optional(),
  compra_id: uuid.optional(),
  producto_id: uuid.optional(),
});
export type GastosFiltros = z.infer<typeof gastosFiltros>;
export const reporteGastosQuery = z.object({ desde: z.iso.date(), hasta: z.iso.date(), sucursal_id: uuid.optional() });

export interface CategoriaGastoDTO {
  id: string;
  nombre: string;
  orden: number;
  activa: boolean;
}

export interface GastoDTO {
  id: string;
  sucursal_id: string;
  sucursal_nombre: string;
  fecha: string;
  categoria_id: string;
  categoria_nombre: string;
  descripcion: string;
  moneda: 'USD' | 'BS';
  monto: string;
  /** Tasa Bs/$ con la que se convirtió el gasto, congelada al registrarlo; `tasa_tipo` dice cuál es. */
  tasa_bcv: string;
  /** tasa2 = tasa 2 (gastos registrados o editados desde el 2026-10-10) · bcv = tasa BCV (los anteriores: no se recalculan). */
  tasa_tipo: 'bcv' | 'tasa2';
  monto_usd: string;
  monto_bs: string;
  metodo: MetodoGasto;
  soporte: string | null;
  personal: boolean;
  origen: 'caja' | 'directo';
  sesion_caja_id: string | null;
  caja_nombre: string | null;
  /** Un gasto anulado sigue en el listado, con su marca, y no suma en ningún total. */
  estado: 'pendiente' | 'aprobado' | 'rechazado' | 'anulado';
  umbral_usd: string;
  requiere_aprobacion: boolean;
  registrado_por_nombre: string;
  aprobado_por_nombre: string | null;
  aprobado_en: string | null;
  comentario: string | null;
  duplicado_de: string | null;
  aviso: string | null;
  creado_en: string;
  /** Anulación de un gasto aprobado (la hace la dueña): quién, cuándo y por qué. */
  anulado_por_nombre: string | null;
  anulado_en: string | null;
  motivo_anulacion: string | null;
  /** Número del gasto («gasto N°» en la ficha del producto). */
  numero: number;
  pagado_a: string | null;
  /** Compra ligada: la que lo trajo (`desde_compra`) o la elegida para repartirlo. */
  compra_id: string | null;
  compra_numero: string | null;
  desde_compra: boolean;
  /** Asociado a productos (suma a su costo real) o gasto general (resta en la ganancia real). */
  asociado: boolean;
  reparto: RepartoFilaDTO[];
  /** Lo que el usuario actual puede hacer. */
  puede_aprobar: boolean;
  puede_editar: boolean;
  puede_anular: boolean;
  puede_cambiar_reparto: boolean;
}

export interface ReporteGastosDTO {
  desde: string;
  hasta: string;
  total_usd: string;
  /** Gastos de la tienda y gastos personales de la dueña, por separado (suman `total_usd`). */
  total_tienda_usd: string;
  total_personal_usd: string;
  por_categoria: { categoria_id: string; categoria_nombre: string; cantidad: number; total_usd: string }[];
  por_sucursal: { sucursal_id: string; sucursal_nombre: string; cantidad: number; total_usd: string }[];
  por_metodo: { metodo: string; cantidad: number; total_usd: string }[];
  pendientes: number;
  /** Asociados a productos (van a su costo real) y gastos generales (restan en la ganancia real): suman `total_usd`. */
  total_asociado_usd: string;
  total_general_usd: string;
}
